/**
 * Checkbox — a lightweight, dependency-free checkbox primitive.
 *
 * The app does not ship `@radix-ui/react-checkbox`, so this is a small
 * controlled `role="checkbox"` button styled with design tokens only
 * (`bg-primary`, `border-input`, `ring-ring`) — no hex / `rgb(` / `hsl(`
 * literals. API mirrors the shadcn checkbox the Merit mockup uses
 * (`checked` + `onCheckedChange`) so ported markup needs no rewrites.
 *
 * Feature: merit-judging-rebuild
 */

import * as React from 'react';
import { CheckIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface CheckboxProps
  extends Omit<React.ComponentProps<'button'>, 'onChange' | 'value' | 'type'> {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

function Checkbox({
  className,
  checked = false,
  onCheckedChange,
  disabled,
  ...props
}: CheckboxProps): JSX.Element {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      data-slot="checkbox"
      data-state={checked ? 'checked' : 'unchecked'}
      disabled={disabled}
      onClick={() => onCheckedChange?.(!checked)}
      className={cn(
        'peer flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs outline-none transition-colors',
        'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked && 'bg-primary border-primary text-primary-foreground',
        className,
      )}
      {...props}
    >
      {checked && <CheckIcon className="size-3.5" />}
    </button>
  );
}

export { Checkbox };
